import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID, TemplateRef } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { filter, debounceTime } from 'rxjs/operators';
import { TourActionExecutorService } from './tour-action-executor.service';
import { LayoutService } from '../layout/layout.service';
import { AuthService } from '../../services/auth.service';
import { PosthogService } from '../../services/posthog.service';
import { RecommendedTourService } from './recommended-tour.service';
import {
  Tour,
  TourStep,
  TourStepAction,
  RouteAction,
  HighlightAction,
  PopoverAction,
  InputAction,
  WaitAction,
  ScrollAction,
  NavigationOption,
  TourState
} from './tour.types';

// Re-export types for convenience
export {
  Tour,
  TourStep,
  TourStepAction,
  RouteAction,
  HighlightAction,
  PopoverAction,
  InputAction,
  WaitAction,
  ScrollAction,
  NavigationOption,
  TourState,
  TourNavigation,
  PopoverAlignment,
  PopoverAttachment,
  PopoverArrowPosition
} from './tour.types';

// ============================================================================
// ADDITIONAL INTERFACES (not in types file)
// ============================================================================

export interface TourMetadata {
  userId?: string;
  startedAt: Date;
  lastUpdatedAt: Date;
  currentStepId: string;
  completedSteps: string[];
  responses: { [key: string]: any };
  timings: { [stepId: string]: number }; // Time spent on each step in ms
}

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private readonly STORAGE_KEY = 'active-tour-state';
  private readonly TOUR_VERSION = '1.1'; // Increment when tour structure changes
  private readonly SAVE_DEBOUNCE_MS = 200;
  
  private saveTimeout: any = null;
  private stepStartTime: number = 0;

  // Template reference from app component
  public tourTemplate: TemplateRef<any> | null = null;

  // State management
  private tourStateSubject = new BehaviorSubject<TourState>({
    tour: null,
    currentStep: null,
    stepIndex: -1,
    isActive: false,
    metadata: null
  });

  public tourState$: Observable<TourState> = this.tourStateSubject.asObservable();

  // Tour completion percentage (persisted across component re-renders)
  // Initialize to 100 to hide "Getting Started" until actual data is loaded
  private completionPercentageSubject = new BehaviorSubject<number>(100);
  public completionPercentage$: Observable<number> = this.completionPercentageSubject.asObservable();

  // Register template from app component
  public registerTourTemplate(template: TemplateRef<any>): void {
    this.tourTemplate = template;
  }

  // Derived observable for layout integration
  public get tourActive$(): Observable<boolean> {
    return new Observable(observer => {
      this.tourState$.subscribe(state => {
        observer.next(state.isActive);
      });
    });
  }

  // Get current completion percentage
  public getCompletionPercentage(): number {
    return this.completionPercentageSubject.value;
  }

  constructor(
    private router: Router,
    private afs: AngularFirestore,
    private actionExecutor: TourActionExecutorService,
    private layoutService: LayoutService,
    private authService: AuthService,
    private posthogService: PosthogService,
    private recommendedTourService: RecommendedTourService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    if (!isPlatformBrowser(platformId)) {
      return;
    }

    // Don't auto-load from storage - tours should always start fresh
    // this.loadFromStorage();
    this.setupQueryParameterListener();
    
    // Register tours with RecommendedTourService for automatic route-based suggestions
    this.recommendedTourService.registerTours(this.TOURS);
  }

  // ============================================================================
  // QUERY PARAMETER LISTENER
  // ============================================================================

  private setupQueryParameterListener(): void {
    // Listen for navigation end events with debounce to prevent flickering
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      debounceTime(150)  // Debounce to prevent rapid tour checks during navigation
    ).subscribe(() => {
      console.log('[TourService] Checking for tour query parameter...');
      // Check for tour query parameter
      const urlTree = this.router.parseUrl(this.router.url);
      const tourId = urlTree.queryParams['tour'];
      
      if (tourId) {
        // Start the tour if it exists
        const tour = this.TOURS.find(t => t.id === tourId);
        if (tour) {
          this.startTour(tourId);
          // Remove the query parameter after starting the tour
          this.router.navigate([], {
            queryParams: { tour: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      }
    });
  }

  // ============================================================================
  // HARD-CODED TOURS
  // ============================================================================
  // Each tour is short and focused on a single page/view.
  // Dashboard tours cover the list/grid view, Detail tours cover the individual item view.
  // ============================================================================

  private readonly TOURS: Tour[] = [
    // ========================================
    // WELCOME TOUR - Platform orientation
    // ========================================
    {
      id: 'welcome',
      name: 'Welcome',
      markTaskCompleteOnFinish: 'welcome',
      description: 'Get oriented with the Platfio platform - dashboards, help, and navigation.',
      icon: 'fa-solid fa-rocket',
      imageId: 'BG33',
      estimatedMinutes: '2-3',
      suggestedRoutes: ['/myagency'],
      steps: [
        {
          id: 'welcome-1',
          type: 'info',
          title: 'Your Dashboards',
          content: '<p>Everything in your agency is accessible via these starter dashboards.</p><p>You can create your own or pin favorites to the menu later.</p>',
          defaultCorner: 'bottom-right',
          actions: [
            { type: 'route', path: '/myagency/dashboards', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false },
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '#dashboards-nav-item', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'welcome-2',
          type: 'info',
          title: 'Get Help',
          content: '<p>Access resources, lessons, and announcements anytime with the help button.</p>',
          targetSelector: '.floating-help-button',
          attachment: 'top',
          alignment: 'end',
          spotlight: '.floating-help-button',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.floating-help-button', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'welcome-3',
          type: 'info',
          title: 'Actions Palette',
          content: '<p>Search, add, edit, or delete anything with the Actions Palette.</p><p>Open with <strong>Option/Alt + Shift + A</strong>.</p>',
          targetSelector: '.actions-item',
          attachment: 'right',
          alignment: 'end',
          spotlight: '.actions-item',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.actions-item', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'welcome-4',
          type: 'info',
          title: 'Quick Access',
          content: '<p>Access settings, profile, and notifications here.</p>',
          targetSelector: '.navigation-menu-footer',
          attachment: 'right',
          alignment: 'end',
          spotlight: '.navigation-menu-footer',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.navigation-menu-footer', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // BUSINESSES DASHBOARD TOUR
    // ========================================
    {
      id: 'businesses-dashboard',
      name: 'Businesses',
      markTaskCompleteOnFinish: 'add-businesses',
      description: 'Learn to navigate and manage your businesses pipeline.',
      icon: 'fa-solid fa-building',
      imageId: 'BG25',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/dashboard/QgDZePpcGlAstzeWQRjF'],
      steps: [
        {
          id: 'biz-dash-1',
          type: 'info',
          title: 'Businesses Dashboard',
          content: '<p>This dashboard shows all businesses in your pipeline.</p>',
          actions: [
            { type: 'route', path: '/myagency/dashboard/QgDZePpcGlAstzeWQRjF', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'biz-dash-2',
          type: 'info',
          title: 'Switch Views',
          content: '<p>Switch between table, kanban, calendar and other layouts.</p>',
          targetSelector: '.custom-header-views-container',
          attachment: 'bottom',
          alignment: 'start',
          spotlight: '.custom-header-views-container',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.custom-header-views-container', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'biz-dash-3',
          type: 'info',
          title: 'Add & Search',
          content: '<p>Press <strong>Add</strong> to create a business, or the <strong>lightning bolt</strong> to search.</p>',
          targetSelector: '.widget-ctas',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.widget-ctas',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.widget-ctas', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // BUSINESS DETAIL TOUR
    // ========================================
    {
      id: 'business-detail',
      name: 'Business',
      description: 'Explore a business profile with proposals, apps, tickets and tasks.',
      icon: 'fa-solid fa-building',
      imageId: 'BG25',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/:businessId'],
      excludeRoutes: [
        '/myagency/dashboard/**',
        '/myagency/setup',
        '/myagency/settings',
        '/myagency/leads',
        '/myagency/marketing',
        '/myagency/tools',
        '/myagency/reports',
        '/myagency/agents',
        '/myagency/accounts',
        '/myagency/team',
        '/myagency/developer-appstore',
        '/myagency/developer-playstore',
        '/myagency/developer-google',
        '/myagency/template/**',
        '/myagency/sequence/**',
        '/myagency/sender/**'
      ],
      steps: [
        {
          id: 'biz-detail-1',
          type: 'info',
          title: 'Business Overview',
          content: '<p>This is the business detail page. View and manage everything for this business in one place.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'biz-detail-2',
          type: 'info',
          title: 'Proposals, Apps & Tickets',
          content: '<p>Access this business\'s proposals, apps, and support tickets from the tabs or sections on this page.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'biz-detail-3',
          type: 'info',
          title: 'Tasks & Activity',
          content: '<p>Track tasks and view activity history to stay on top of everything.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // APPS DASHBOARD TOUR
    // ========================================
    {
      id: 'apps-dashboard',
      name: 'Apps',
      markTaskCompleteOnFinish: 'launch-app',
      description: 'Navigate and manage all apps across your agency.',
      icon: 'fa-solid fa-mobile-screen',
      imageId: 'BG09',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/dashboard/ymPnupS8lfh6ZzrpBiFu'],
      steps: [
        {
          id: 'apps-dash-1',
          type: 'info',
          title: 'Apps Dashboard',
          content: '<p>This dashboard shows all apps across your agency, organized by business.</p>',
          actions: [
            { type: 'route', path: '/myagency/dashboard/ymPnupS8lfh6ZzrpBiFu', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'apps-dash-2',
          type: 'info',
          title: 'Switch Views',
          content: '<p>Switch between table, kanban, and other layouts to find what works best.</p>',
          targetSelector: '.custom-header-views-container',
          attachment: 'bottom',
          alignment: 'start',
          spotlight: '.custom-header-views-container',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.custom-header-views-container', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'apps-dash-3',
          type: 'info',
          title: 'Add & Search',
          content: '<p>Press <strong>Add</strong> to create an app, or the <strong>lightning bolt</strong> to search.</p>',
          targetSelector: '.widget-ctas',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.widget-ctas',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.widget-ctas', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // APP DETAIL TOUR
    // ========================================
    {
      id: 'app-detail',
      name: 'App',
      description: 'Explore an app with builds, versions, and the live emulator.',
      icon: 'fa-solid fa-mobile-screen',
      imageId: 'BG09',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/app/:appId'],
      steps: [
        {
          id: 'app-detail-1',
          type: 'info',
          title: 'App Overview',
          content: '<p>This is the app detail page. View builds, versions, and settings all in one place.</p>',
          defaultCorner: 'top-right',
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'app-detail-2',
          type: 'info',
          title: 'Live Emulator',
          content: '<p>For Platfio apps, press the manage button to launch the live emulator and test in real-time.</p>',
          targetSelector: '#manage-app-button',
          attachment: 'left',
          alignment: 'center',
          spotlight: '#manage-app-button',
          actions: [{ type: 'wait', duration: 300 }],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'app-detail-3',
          type: 'info',
          title: 'Deploy Your App',
          content: '<p>Once ready, deploy to iOS, Android, and web from this page.</p>',
          defaultCorner: 'bottom-left',
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // PROPOSALS DASHBOARD TOUR
    // ========================================
    {
      id: 'proposals-dashboard',
      name: 'Proposals',
      markTaskCompleteOnFinish: 'manage-proposal',
      description: 'Navigate and track all proposals across your agency.',
      icon: 'fa-solid fa-file-invoice',
      imageId: 'BG23',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/dashboard/F1Htu56VZEqbhIsecIbI'],
      steps: [
        {
          id: 'prop-dash-1',
          type: 'info',
          title: 'Proposals Dashboard',
          content: '<p>This dashboard shows all proposals and their status across your agency.</p>',
          actions: [
            { type: 'route', path: '/myagency/dashboard/F1Htu56VZEqbhIsecIbI', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'prop-dash-2',
          type: 'info',
          title: 'Switch Views',
          content: '<p>Switch between table, kanban, and other layouts to track proposal progress.</p>',
          targetSelector: '.custom-header-views-container',
          attachment: 'bottom',
          alignment: 'start',
          spotlight: '.custom-header-views-container',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.custom-header-views-container', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'prop-dash-3',
          type: 'info',
          title: 'Add & Search',
          content: '<p>Press <strong>Add</strong> to create a proposal, or the <strong>lightning bolt</strong> to search.</p>',
          targetSelector: '.widget-ctas',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.widget-ctas',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.widget-ctas', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // PROPOSAL DETAIL TOUR
    // ========================================
    {
      id: 'proposal-detail',
      name: 'Proposal',
      description: 'Edit, preview, and send proposals to customers.',
      icon: 'fa-solid fa-file-invoice',
      imageId: 'BG23',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/:businessId/:proposalId'],
      excludeRoutes: [
        '/myagency/dashboard/**',
        '/myagency/template/**',
        '/myagency/sequence/**',
        '/myagency/sender/**',
        '/myagency/resources/**',
        '/myagency/landings/**',
        '/myagency/forms/**',
        '/myagency/terms/**',
        '/myagency/:businessId/wireframes'
      ],
      steps: [
        {
          id: 'prop-detail-1',
          type: 'info',
          title: 'Proposal Overview',
          content: '<p>Edit pricing, features, and terms for this proposal.</p>',
          defaultCorner: 'bottom-left',
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'prop-detail-2',
          type: 'info',
          title: 'Customer Preview',
          content: '<p>Preview exactly how the customer will see the proposal before sending.</p>',
          defaultCorner: 'bottom-left',
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'prop-detail-3',
          type: 'info',
          title: 'Send Proposal',
          content: '<p>When ready, send the proposal directly to your customer for review and signature.</p>',
          targetSelector: '.send-proposal-btn',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.send-proposal-btn',
          actions: [{ type: 'wait', duration: 300 }],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // TICKETS DASHBOARD TOUR
    // ========================================
    {
      id: 'tickets-dashboard',
      name: 'Tickets',
      markTaskCompleteOnFinish: 'manage-tickets',
      description: 'Navigate and manage support tickets across your agency.',
      icon: 'fa-solid fa-ticket',
      imageId: 'BG08',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/dashboard/RZhNS1Z7BaCRfCDh1BRJ'],
      steps: [
        {
          id: 'tix-dash-1',
          type: 'info',
          title: 'Tickets Dashboard',
          content: '<p>This dashboard shows all support tickets across your agency.</p>',
          actions: [
            { type: 'route', path: '/myagency/dashboard/RZhNS1Z7BaCRfCDh1BRJ', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'tix-dash-2',
          type: 'info',
          title: 'Switch Views',
          content: '<p>Switch between table, kanban, and other layouts to manage ticket flow.</p>',
          targetSelector: '.custom-header-views-container',
          attachment: 'bottom',
          alignment: 'start',
          spotlight: '.custom-header-views-container',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.custom-header-views-container', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'tix-dash-3',
          type: 'info',
          title: 'Add & Search',
          content: '<p>Press <strong>Add</strong> to create a ticket, or the <strong>lightning bolt</strong> to search.</p>',
          targetSelector: '.widget-ctas',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.widget-ctas',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.widget-ctas', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // TICKET DETAIL TOUR
    // ========================================
    {
      id: 'ticket-detail',
      name: 'Ticket',
      description: 'View and respond to support tickets.',
      icon: 'fa-solid fa-ticket',
      imageId: 'BG08',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/ticket/:ticketId'],
      steps: [
        {
          id: 'tix-detail-1',
          type: 'info',
          title: 'Ticket Conversation',
          content: '<p>View the conversation between you and the customer. See issue details and resolution status.</p>',
          defaultCorner: 'top-right',
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'tix-detail-2',
          type: 'info',
          title: 'Rich Attachments',
          content: '<p>Attach screenshots, screen recordings, audio, or links to help resolve issues faster.</p>',
          targetSelector: '.fe-paperclip',
          attachment: 'top',
          alignment: 'center',
          spotlight: '.fe-paperclip',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.fe-paperclip', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'tix-detail-3',
          type: 'info',
          title: 'Resolve & Close',
          content: '<p>Update the ticket status and close it when the issue is resolved.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // TASKS DASHBOARD TOUR
    // ========================================
    {
      id: 'tasks-dashboard',
      name: 'Tasks',
      markTaskCompleteOnFinish: 'manage-tasks',
      description: 'Navigate and manage tasks across your agency.',
      icon: 'fa-solid fa-square-check',
      imageId: 'BG14',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/dashboard/rhPZa4vSV3OGNX1yvfe6'],
      steps: [
        {
          id: 'tasks-dash-1',
          type: 'info',
          title: 'Tasks Dashboard',
          content: '<p>This dashboard shows all tasks across your agency.</p>',
          actions: [
            { type: 'route', path: '/myagency/dashboard/rhPZa4vSV3OGNX1yvfe6', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'tasks-dash-2',
          type: 'info',
          title: 'Switch Views',
          content: '<p>Switch between table, kanban, calendar and other layouts.</p>',
          targetSelector: '.custom-header-views-container',
          attachment: 'bottom',
          alignment: 'start',
          spotlight: '.custom-header-views-container',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.custom-header-views-container', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'tasks-dash-3',
          type: 'info',
          title: 'Add & Search',
          content: '<p>Press <strong>Add</strong> to create a task, or the <strong>lightning bolt</strong> to search.</p>',
          targetSelector: '.widget-ctas',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.widget-ctas',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.widget-ctas', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // TASK DETAIL TOUR
    // ========================================
    {
      id: 'task-detail',
      name: 'Task',
      description: 'View and manage individual tasks with subtasks and time tracking.',
      icon: 'fa-solid fa-square-check',
      imageId: 'BG14',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/task/:taskId'],
      steps: [
        {
          id: 'task-detail-1',
          type: 'info',
          title: 'Task Overview',
          content: '<p>View task details, description, and assignment information.</p>',
          defaultCorner: 'top-right',
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'task-detail-2',
          type: 'info',
          title: 'Subtasks',
          content: '<p>Break down complex tasks into smaller subtasks for better tracking.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'task-detail-3',
          type: 'info',
          title: 'Time Tracking',
          content: '<p>Log time spent on tasks to track productivity and bill clients.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // PLANS DASHBOARD TOUR
    // ========================================
    {
      id: 'plans-dashboard',
      name: 'Plans',
      markTaskCompleteOnFinish: 'manage-plans',
      description: 'Navigate and manage app plans and specifications across your agency.',
      icon: 'fa-solid fa-sitemap',
      imageId: 'BG16',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/dashboard/jBWL8FyqJ0coT8CvQfBs'],
      steps: [
        {
          id: 'plans-dash-1',
          type: 'info',
          title: 'Plans Dashboard',
          content: '<p>This dashboard shows all app plans and specifications across your agency.</p>',
          actions: [
            { type: 'route', path: '/myagency/dashboard/jBWL8FyqJ0coT8CvQfBs', queryParams: { uid: '{{agencyId}}' }, waitForLoad: false }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'plans-dash-2',
          type: 'info',
          title: 'Switch Views',
          content: '<p>Switch between table, kanban, and other layouts to organize your plans.</p>',
          targetSelector: '.custom-header-views-container',
          attachment: 'bottom',
          alignment: 'start',
          spotlight: '.custom-header-views-container',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.custom-header-views-container', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'plans-dash-3',
          type: 'info',
          title: 'Add & Search',
          content: '<p>Press <strong>Add</strong> to create a plan, or the <strong>lightning bolt</strong> to search.</p>',
          targetSelector: '.widget-ctas',
          attachment: 'bottom',
          alignment: 'end',
          spotlight: '.widget-ctas',
          actions: [
            { type: 'wait', duration: 300 },
            { type: 'highlight', selector: '.widget-ctas', style: 'border', color: 'purple' }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // PLAN DETAIL TOUR
    // ========================================
    {
      id: 'plan-detail',
      name: 'Plan',
      description: 'Create and manage app specifications with flows, personas, and objects.',
      icon: 'fa-solid fa-sitemap',
      imageId: 'BG16',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/myagency/:businessId/wireframes'],
      steps: [
        {
          id: 'plan-detail-1',
          type: 'info',
          title: 'Plan Overview',
          content: '<p>Define your app\'s structure, features, and requirements here.</p>',
          defaultCorner: 'top-right',
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'plan-detail-2',
          type: 'info',
          title: 'User Flows',
          content: '<p>Map out user journeys and screen flows for your app.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'plan-detail-3',
          type: 'info',
          title: 'Data Objects',
          content: '<p>Define the data structure and objects your app will use.</p>',
          defaultCorner: 'bottom-right',
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    },

    // ========================================
    // WHAT'S NEXT TOUR - Pricing & Resources
    // ========================================
    {
      id: 'whats-next',
      name: 'What\'s Next',
      markTaskCompleteOnFinish: 'whats-next',
      description: 'Explore pricing, book demos, and discover resources.',
      icon: 'fa-solid fa-forward',
      imageId: 'BG24',
      estimatedMinutes: '1-2',
      suggestedRoutes: ['/help-center'],
      steps: [
        {
          id: 'next-1',
          type: 'info',
          title: 'Agency Pricing',
          content: '<p>View pricing for using Platfio as an agency. Manage unlimited businesses, proposals, and tickets.</p>',
          actions: [
            { type: 'route', path: '/help-center', queryParams: { tab: 'pricing', type: 'agency' }, waitForLoad: true }
          ],
          navigation: { type: 'buttons', previousEnabled: false }
        },
        {
          id: 'next-2',
          type: 'info',
          title: 'App Pricing',
          content: '<p>If you build apps with Platfio, view per-app pricing here.</p>',
          actions: [
            { type: 'route', path: '/help-center', queryParams: { tab: 'pricing', type: 'app' }, waitForLoad: true }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        },
        {
          id: 'next-3',
          type: 'info',
          title: 'Book a Demo',
          content: '<p>Schedule a personalized demo with the Platfio team.</p>',
          allowBackgroundInteraction: true,
          actions: [
            { type: 'route', path: '/help-center', queryParams: { tab: 'bookings' }, waitForLoad: true }
          ],
          navigation: { type: 'buttons', previousEnabled: true }
        }
      ]
    }
  ];


  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get all available tours
   */
  getAllTours(): Tour[] {
    return this.TOURS;
  }

  /**
   * Get tour by ID
   */
  getTourById(tourId: string): Tour | undefined {
    return this.TOURS.find(t => t.id === tourId);
  }

  /**
   * Start a tour
   */
  async startTour(tourId: string): Promise<void> {
    const tour = this.getTourById(tourId);
    if (!tour) return;
    
    // Track tour start in PostHog
    this.posthogService.capture('tour_started', {
      tour_id: tourId,
      tour_name: tour.name,
      total_steps: tour.steps.length
    });
    
    // Tours now use popover display - no need to manage right panel
    
    // Clear any existing tour
    this.actionExecutor.clearActions();

    const state: TourState = {
      tour,
      currentStep: tour.steps[0],
      stepIndex: 0,
      isActive: true,
      metadata: {
        startedAt: new Date(),
        lastUpdatedAt: new Date(),
        currentStepId: tour.steps[0].id,
        completedSteps: [],
        responses: {},
        timings: {}
      }
    };

    this.updateState(state);
    
    // Execute first step actions
    if (tour.steps[0].actions && tour.steps[0].actions.length > 0) {
      await this.actionExecutor.executeActions(tour.steps[0].actions);
    }

    // Show spotlight for first step if it has one
    if (tour.steps[0].spotlight) {
      this.actionExecutor.showSpotlight(tour.steps[0].spotlight);
    }
  }

  /**
   * Navigate to next step
   */
  async nextStep(): Promise<void> {
    const state = this.tourStateSubject.value;
    
    if (!state.tour || !state.metadata) return;

    const currentStep = state.currentStep;
    const nextIndex = state.stepIndex + 1;
    
    // Check if we're at the end
    if (nextIndex >= state.tour.steps.length) {
      await this.completeTour();
      return;
    }

    // Remove spotlight from current step if it had one
    if (currentStep?.spotlight) {
      this.actionExecutor.removeSpotlight();
    }

    // Execute beforeLeaveActions on the current step before navigating away
    if (currentStep?.beforeLeaveActions && currentStep.beforeLeaveActions.length > 0) {
      try {
        await this.actionExecutor.executeActions(currentStep.beforeLeaveActions);
      } catch (error) {
        console.error('[TourService] Failed to execute beforeLeaveActions:', error);
      }
    }

    const nextStep = state.tour.steps[nextIndex];

    // Execute preActions BEFORE updating state/displaying step
    if (nextStep.preActions && nextStep.preActions.length > 0) {
      try {
        await this.actionExecutor.executeActions(nextStep.preActions);
      } catch (error) {
        console.error('[TourService] Failed to execute preActions:', error);
      }
    } else {
    }

    // Execute routing actions BEFORE updating state so the route changes before popover shows
    if (nextStep.actions && nextStep.actions.length > 0) {
      const routingActions = nextStep.actions.filter(action => action.type === 'route');
      if (routingActions.length > 0) {
        try {
          await this.actionExecutor.executeActions(routingActions);
        } catch (error) {
          console.error('[TourService] Failed to execute routing actions:', error);
        }
      }
    }

    const newState: TourState = {
      ...state,
      currentStep: nextStep,
      stepIndex: nextIndex,
      metadata: {
        ...state.metadata,
        currentStepId: nextStep.id,
        completedSteps: [...state.metadata.completedSteps, state.currentStep?.id || ''],
        lastUpdatedAt: new Date()
      }
    };

    this.updateState(newState);
    
    // Execute remaining non-routing actions for the new step (e.g., highlights, keyboard)
    if (nextStep.actions && nextStep.actions.length > 0) {
      const nonRoutingActions = nextStep.actions.filter(action => action.type !== 'route');
      if (nonRoutingActions.length > 0) {
        await this.actionExecutor.executeActions(nonRoutingActions);
      }
    }

    // Show spotlight for the new step if it has one
    if (nextStep.spotlight) {
      this.actionExecutor.showSpotlight(nextStep.spotlight);
    }
  }

  /**
   * Navigate to previous step
   */
  async previousStep(): Promise<void> {
    const state = this.tourStateSubject.value;
    if (!state.tour || !state.metadata || state.stepIndex === 0) return;

    const currentStep = state.currentStep;
    const prevIndex = state.stepIndex - 1;
    const prevStep = state.tour.steps[prevIndex];

    // Remove spotlight from current step if it had one
    if (currentStep?.spotlight) {
      this.actionExecutor.removeSpotlight();
    }

    // Execute preActions BEFORE updating state/displaying step (e.g., Firestore queries)
    if (prevStep.preActions && prevStep.preActions.length > 0) {
      try {
        await this.actionExecutor.executeActions(prevStep.preActions);
      } catch (error) {
        console.error('[TourService] Failed to execute preActions for previous step:', error);
      }
    }

    // Execute routing actions BEFORE updating state so the route changes before popover shows
    if (prevStep.actions && prevStep.actions.length > 0) {
      const routingActions = prevStep.actions.filter(action => action.type === 'route');
      if (routingActions.length > 0) {
        try {
          await this.actionExecutor.executeActions(routingActions);
        } catch (error) {
          console.error('[TourService] Failed to execute routing actions for previous step:', error);
        }
      }
    }

    const newState: TourState = {
      ...state,
      currentStep: prevStep,
      stepIndex: prevIndex,
      metadata: {
        ...state.metadata,
        currentStepId: prevStep.id,
        lastUpdatedAt: new Date()
      }
    };

    this.updateState(newState);
    
    // Execute remaining non-routing actions when previous step loads (e.g., highlights, keyboard)
    if (prevStep.actions && prevStep.actions.length > 0) {
      const nonRoutingActions = prevStep.actions.filter(action => action.type !== 'route');
      if (nonRoutingActions.length > 0) {
        await this.actionExecutor.executeActions(nonRoutingActions);
      }
    }

    // Show spotlight for the previous step if it has one
    if (prevStep.spotlight) {
      this.actionExecutor.showSpotlight(prevStep.spotlight);
    }
  }

  /**
   * Go to specific step by ID
   */
  async goToStep(stepId: string): Promise<void> {
    const state = this.tourStateSubject.value;
    if (!state.tour || !state.metadata) return;

    // Remove spotlight from current step if it had one
    if (state.currentStep?.spotlight) {
      this.actionExecutor.removeSpotlight();
    }

    const stepIndex = state.tour.steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;

    const step = state.tour.steps[stepIndex];

    // Check condition
    if (step.condition && !step.condition(state.metadata)) {
      console.warn(`Step ${stepId} condition not met`);
      return;
    }

    this.recordStepTiming(state.currentStep?.id);

    // Execute preActions before displaying the step (e.g., Firestore queries)
    if (step.preActions && step.preActions.length > 0) {
      try {
        await this.actionExecutor.executeActions(step.preActions);
      } catch (error) {
        console.error(`Failed to execute preActions for step ${step.id}:`, error);
      }
    }

    const newState: TourState = {
      ...state,
      currentStep: step,
      stepIndex,
      metadata: {
        ...state.metadata,
        currentStepId: step.id,
        lastUpdatedAt: new Date()
      }
    };

    this.stepStartTime = Date.now();
    this.updateState(newState);

    // Execute actions for the step
    if (step.actions && step.actions.length > 0) {
      this.actionExecutor.executeActions(step.actions)
        .then(() => {
          console.log(`Executed ${step.actions!.length} actions for step ${step.id}`);
          // Show spotlight after actions complete if step has one
          if (step.spotlight) {
            this.actionExecutor.showSpotlight(step.spotlight);
          }
        })
        .catch((error: any) => console.error(`Failed to execute actions for step ${step.id}:`, error));
    } else if (step.spotlight) {
      // No actions but has spotlight
      this.actionExecutor.showSpotlight(step.spotlight);
    }
  }

  /**
   * Select an option from form-select navigation
   */
  async selectOption(optionId: string): Promise<void> {
    const state = this.tourStateSubject.value;
    if (!state.currentStep || !state.metadata) return;

    const navigation = state.currentStep.navigation;
    if (navigation.type !== 'form-select' || !navigation.options) return;

    const option = navigation.options.find(o => o.id === optionId);
    if (!option) return;

    // Update metadata with response
    const updatedResponses = { ...state.metadata.responses };
    if (option.metadataUpdate) {
      updatedResponses[option.metadataUpdate.key] = option.metadataUpdate.value;
    }

    const newMetadata: TourMetadata = {
      ...state.metadata,
      responses: updatedResponses,
      lastUpdatedAt: new Date()
    };

    // Navigate to next step specified by option
    if (option.nextStepId) {
      const newState: TourState = {
        ...state,
        metadata: newMetadata
      };
      this.updateState(newState);
      await this.goToStep(option.nextStepId);
    } else {
      // No specific next step, use default next
      const newState: TourState = {
        ...state,
        metadata: newMetadata
      };
      this.updateState(newState);
      await this.nextStep();
    }

    this.trackAnalytics('option_selected', { optionId, stepId: state.currentStep.id });
  }

  /**
   * Pause the tour
   */
  pauseTour(): void {
    const state = this.tourStateSubject.value;
    if (!state.isActive) return;

    this.recordStepTiming(state.currentStep?.id);

    const newState: TourState = {
      ...state,
      isActive: false
    };

    this.updateState(newState);
    this.trackAnalytics('tour_paused', { tourId: state.tour?.id });
  }

  /**
   * Resume the tour
   */
  resumeTour(): void {
    const state = this.tourStateSubject.value;
    if (state.isActive || !state.tour) return;

    this.stepStartTime = Date.now();

    const newState: TourState = {
      ...state,
      isActive: true
    };

    this.updateState(newState);
    this.trackAnalytics('tour_resumed', { tourId: state.tour.id });
  }

  /**
   * Complete the tour
   */
  async completeTour(): Promise<void> {
    const currentState = this.tourStateSubject.value;
    const shouldClosePanel = currentState.tour?.closePanelOnFinish;
    const tourId = currentState.tour?.id;
    const taskIdToComplete = currentState.tour?.markTaskCompleteOnFinish;
    const nextTourId = currentState.tour?.startTourOnFinish;
    
    // Track tour completion in PostHog
    if (tourId && currentState.tour) {
      this.posthogService.capture('tour_completed', {
        tour_id: tourId,
        tour_name: currentState.tour.name,
        total_steps: currentState.tour.steps.length,
        completed_steps: currentState.metadata?.completedSteps?.length || 0
      });
    }
    
    // Save completion to Firebase
    if (tourId) {
      await this.markTourAsCompleted(tourId);
    }
    
    // Mark associated task as complete if specified
    if (taskIdToComplete) {
      await this.markTaskAsComplete(taskIdToComplete);
    }
    
    this.clearTour();
    
    // Close the detail panel if the tour has the flag set
    if (shouldClosePanel && this.layoutService.rightOpen) {
      this.layoutService.toggleRightPanel();
    }
    
    // Start the next tour if specified
    if (nextTourId) {
      // Small delay to allow UI to reset before starting next tour
      setTimeout(() => {
        this.startTour(nextTourId);
      }, 500);
    }
  }

  /**
   * Skip a tour - marks it as completed without going through all steps
   */
  async skipTour(tourId: string): Promise<void> {
    const tour = this.TOURS.find(t => t.id === tourId);
    if (!tour) {
      console.warn(`Tour ${tourId} not found`);
      return;
    }

    // Track tour skip in PostHog
    this.posthogService.capture('tour_skipped', {
      tour_id: tourId,
      tour_name: tour.name,
      total_steps: tour.steps.length
    });

    // Mark tour as completed
    await this.markTourAsCompleted(tourId);

    // Mark associated task as complete if specified
    if (tour.markTaskCompleteOnFinish) {
      await this.markTaskAsComplete(tour.markTaskCompleteOnFinish);
    }

    console.log(`Tour ${tourId} skipped and marked as completed`);
  }

  /**
   * Reset a specific tour (clear progress)
   */
  resetTour(tourId: string): void {
    const state = this.tourStateSubject.value;
    if (state.tour?.id === tourId) {
      this.clearTour();
    }
    // Also clear from localStorage if it exists
    this.clearFromStorage();
  }

  closeTour(): void {
    this.clearTour();
  }

  /**
   * Get current tour state
   */
  getCurrentState(): TourState {
    return this.tourStateSubject.value;
  }

  // ============================================================================
  // PRIVATE HELPERS
  // ============================================================================

  private findNextStep(startIndex: number, tour: Tour, metadata: TourMetadata): { step: TourStep; index: number } | null {
    console.log('[Tour] findNextStep called with startIndex:', startIndex, 'tour has', tour.steps.length, 'steps');
    for (let i = startIndex; i < tour.steps.length; i++) {
      const step = tour.steps[i];
      console.log('[Tour] Checking step at index', i, '- stepId:', step.id, 'has condition:', !!step.condition);
      if (!step.condition || step.condition(metadata)) {
        console.log('[Tour] Found next step:', step.id, 'at index', i);
        return { step, index: i };
      }
    }
    console.log('[Tour] No next step found - tour should complete');
    return null;
  }

  private findPreviousStep(startIndex: number, tour: Tour, metadata: TourMetadata): { step: TourStep; index: number } | null {
    for (let i = startIndex; i >= 0; i--) {
      const step = tour.steps[i];
      if (!step.condition || step.condition(metadata)) {
        return { step, index: i };
      }
    }
    return null;
  }

  private recordStepTiming(stepId: string | undefined): void {
    if (!stepId || !this.stepStartTime) return;

    const state = this.tourStateSubject.value;
    if (!state.metadata) return;

    const duration = Date.now() - this.stepStartTime;
    const newMetadata: TourMetadata = {
      ...state.metadata,
      timings: {
        ...state.metadata.timings,
        [stepId]: duration
      }
    };

    const newState: TourState = {
      ...state,
      metadata: newMetadata
    };

    this.tourStateSubject.next(newState);
  }

  private updateState(state: TourState): void {
    this.tourStateSubject.next(state);
    this.debouncedSaveToStorage(state);
  }

  private clearTour(): void {
    this.actionExecutor.clearActions();
    
    const emptyState: TourState = {
      tour: null,
      currentStep: null,
      stepIndex: -1,
      isActive: false,
      metadata: null
    };
    this.tourStateSubject.next(emptyState);
    this.clearFromStorage();
  }

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  private debouncedSaveToStorage(state: TourState): void {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
    this.saveTimeout = setTimeout(() => {
      this.saveToStorage(state);
      this.saveTimeout = null;
    }, this.SAVE_DEBOUNCE_MS);
  }

  private saveToStorage(state: TourState): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const serialized = JSON.stringify({
        tourId: state.tour?.id,
        stepId: state.currentStep?.id,
        stepIndex: state.stepIndex,
        isActive: state.isActive,
        metadata: state.metadata
      });
      localStorage.setItem(this.STORAGE_KEY, serialized);
    } catch (error) {
      console.warn('Failed to save tour state to localStorage:', error);
    }
  }

  private loadFromStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return;

      const data = JSON.parse(stored);
      if (!data.tourId || !data.isActive) return;

      const tour = this.getTourById(data.tourId);
      if (!tour) return;

      const step = tour.steps.find(s => s.id === data.stepId);
      if (!step) return;

      this.stepStartTime = Date.now();

      const state: TourState = {
        tour,
        currentStep: step,
        stepIndex: data.stepIndex,
        isActive: data.isActive,
        metadata: data.metadata
      };

      this.tourStateSubject.next(state);
    } catch (error) {
      console.warn('Failed to load tour state from localStorage:', error);
    }
  }

  private clearFromStorage(): void {
    if (typeof localStorage === 'undefined') return;

    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear tour state from localStorage:', error);
    }
  }

  // ============================================================================
  // ANALYTICS (DUMMY IMPLEMENTATION)
  // ============================================================================

  private trackAnalytics(event: string, data: any): void {
    // Dummy implementation - save to Firestore
    const state = this.tourStateSubject.value;
    if (!state.metadata?.userId || !state.tour) return;

    const analyticsData = {
      event,
      tourId: state.tour.id,
      stepId: state.currentStep?.id,
      timestamp: new Date(),
      data,
      metadata: state.metadata
    };

    // Save to Firestore - security rules to be added later
    this.afs
      .collection('users')
      .doc(state.metadata.userId)
      .collection('tourProgress')
      .doc(state.tour.id)
      .set(
        {
          tourId: state.tour.id,
          lastEvent: event,
          lastUpdatedAt: new Date(),
          events: analyticsData
        },
        { merge: true }
      )
      .catch(error => {
        console.warn('Failed to track tour analytics:', error);
      });
  }

  // ============================================================================
  // TOUR COMPLETION TRACKING
  // ============================================================================

  /**
   * Mark a tour as completed in Firebase
   */
  async markTourAsCompleted(tourId: string): Promise<void> {
    const user = this.authService.authStateUser;
    if (!user) {
      console.warn('Cannot save tour completion - user not authenticated');
      return;
    }

    try {
      await this.afs
        .collection('users')
        .doc(user.uid)
        .collection('tours')
        .doc(tourId)
        .set({
          tourId,
          completed: true,
          completedAt: new Date(),
          lastUpdatedAt: new Date()
        }, { merge: true });
      
      console.log(`Tour ${tourId} marked as completed`);
    } catch (error) {
      console.error('Failed to save tour completion:', error);
    }
  }

  /**
   * Get list of completed tour IDs for current user
   */
  async getCompletedTours(): Promise<string[]> {
    const user = this.authService.authStateUser;
    if (!user) {
      return [];
    }

    try {
      const snapshot = await this.afs
        .collection('users')
        .doc(user.uid)
        .collection('tours')
        .ref
        .where('completed', '==', true)
        .get();

      return snapshot.docs.map(doc => doc.data()['tourId'] as string);
    } catch (error) {
      console.error('Failed to load completed tours:', error);
      return [];
    }
  }

  /**
   * Observe completed tour IDs for current user (real-time updates)
   */
  observeCompletedTours(): Observable<string[]> {
    const user = this.authService.authStateUser;
    
    if (!user) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    return new Observable(observer => {
      const unsubscribe = this.afs
        .collection('users')
        .doc(user.uid)
        .collection('tours')
        .ref
        .where('completed', '==', true)
        .onSnapshot(
          (snapshot) => {
            const completedIds = snapshot.docs.map(doc => doc.data()['tourId'] as string);
            
            // Update the completion percentage in the service
            const allTours = this.getAllTours();
            const percentage = allTours.length > 0 
              ? Math.round((completedIds.length / allTours.length) * 100)
              : 0;
            this.completionPercentageSubject.next(percentage);
            
            observer.next(completedIds);
          },
          (error) => {
            console.error('[TourService] Failed to observe completed tours:', error);
            observer.error(error);
          }
        );

      return () => unsubscribe();
    });
  }

  /**
   * Delete tour completion record (for testing)
   */
  async deleteTourCompletion(tourId: string): Promise<void> {
    const user = this.authService.authStateUser;
    if (!user) {
      console.warn('Cannot delete tour completion - user not authenticated');
      return;
    }

    try {
      await this.afs
        .collection('users')
        .doc(user.uid)
        .collection('tours')
        .doc(tourId)
        .delete();
      
      console.log(`Tour ${tourId} completion record deleted`);
    } catch (error) {
      console.error('Failed to delete tour completion:', error);
    }
  }

  /**
   * Mark a task as complete
   */
  async markTaskAsComplete(taskId: string): Promise<void> {
    const user = this.authService.authStateUser;
    if (!user) {
      console.warn('Cannot mark task complete - user not authenticated');
      return;
    }

    try {
      await this.afs
        .collection('users')
        .doc(user.uid)
        .collection('tasks')
        .doc(taskId)
        .update({
          complete: true,
          completedAt: new Date()
        });
      
      console.log(`Task ${taskId} marked as complete`);
    } catch (error) {
      console.error('Failed to mark task as complete:', error);
    }
  }
}
